import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService } from '../services/api';
import { projectsService } from '../services/projectsService';
import GamePreview, { GamePreviewHandle } from '../components/editor/GamePreview';
import EditModePreview, { EditModePreviewHandle } from '../components/editor/EditModePreview';
import CodeEditor from '../components/editor/CodeEditor';
import AIPromptInput, { AIPromptInputHandle } from '../components/editor/AIPromptInput';
import ChatHistory from '../components/editor/ChatHistory';
import SuggestionsList from '../components/editor/SuggestionsList';
import EditHistory from '../components/editor/EditHistory';
import ReviewSection from '../components/editor/ReviewSection';
import LivestreamSimulator from '../components/LivestreamSimulator';
import AssetBrowser from '../components/editor/AssetBrowser';
import ProjectDetailsForm from '../components/editor/ProjectDetailsForm';
import PublishModal from '../components/editor/PublishModal';
import { ProjectEditModal } from '../components/projects/ProjectEditModal';
import EditorToast from '../components/editor/EditorToast';
import CreatingGameModal from '../components/common/CreatingGameModal';
import { GenerationFailedDialog } from '../components/common/GenerationFailedDialog';
import { PropertiesPanel } from '../components/editor/properties/PropertiesPanel';
import ErrorPanel from '../components/editor/ErrorPanel';
import AccessDeniedPanel from '../components/editor/AccessDeniedPanel';
import STARTER_TEMPLATE from '../templates/starter-game.html?raw';
import { AIModel } from '../components/editor/ModelSelector';
import { VisualStyle } from '../components/editor/StyleSelector';
import authService from '../services/authService';
import { User } from '../services/userService';
import { getEditsFromIframe } from '../utils/editApplicator';
import { useCategoriesStore } from '../store/useCategoriesStore';
import { Sparkles, History, Eye, EyeOff, RotateCw, Code, Play, MessageSquare, Check, Pencil, Camera, FolderOpen, MessagesSquare, ClipboardList, Volume2, VolumeX } from 'lucide-react';
import beemiLogo from '../assets/icons/beemi-logo.png';
import tiktokIcon from '../assets/icons/platform-tiktok.png';
import emulatorOverlay from '../assets/overlays/emulator-overlay-aspect-1_2.png';
import { LINE_GRADIENT, FILL_GRADIENT } from '../constants/gradients';
import { useProjectManagement } from '../hooks/useProjectManagement';
import { useUsageTracking } from '../hooks/useUsageTracking';
import { useEditHistory } from '../hooks/useEditHistory';
import { useReviewManagement } from '../hooks/useReviewManagement';
import { useScreenshots } from '../hooks/useScreenshots';
import { useProcessingMessages } from '../hooks/useProcessingMessages';
import { useConversation } from '../hooks/useConversation';
import { useVisualEditor } from '../hooks/useVisualEditor';
import { useAIGeneration } from '../hooks/useAIGeneration';
import { analyticsService } from '../services/analyticsService';
import { sdkService } from '../services/sdkService';
import { generateDefaultManifest } from '../utils/manifestGenerator';
import studioPreviewService from '../services/studioPreviewService';

// Editor Configuration Constants
const EDITOR_CONFIG = {
  // API Timeouts
  AI_GENERATION_TIMEOUT: 240000,      // 2 minutes - for initial game generation
  AI_PATCH_TIMEOUT: 240000,           // 4 minutes - for AI code modifications (matches server timeout)
  
  // UI Delays
  PREFERENCES_SAVE_DELAY: 500,        // Debounce delay for saving preferences
  SUCCESS_NOTIFICATION_DURATION: 3000, // How long to show success notifications
  HISTORY_FETCH_DELAY: 1000,          // Delay before fetching chat history after errors
} as const;

const EditorPage = () => {
  const navigate = useNavigate();
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectId = projectIdParam || null; // Convert undefined to null
  
  // Code and UI state
  const [code, setCode] = useState(STARTER_TEMPLATE);
  const [prompt, setPrompt] = useState('');
  const [previewKey, setPreviewKey] = useState(0);
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  
  // Console errors from game preview
  const [consoleErrors, setConsoleErrors] = useState<string[]>([]);
  
  // Toast notification state
  const [toastConfig, setToastConfig] = useState<{
    type: 'success' | 'error' | 'info' | 'warning';
    message: string;
    isVisible: boolean;
  } | null>(null);
  
  // Navigation state
  const [activeSection, setActiveSection] = useState<'game' | 'assets' | 'description' | 'review'>('game');
  const [completedSections, setCompletedSections] = useState<Set<'game' | 'assets' | 'description' | 'review'>>(new Set());
  const [activeTab, setActiveTab] = useState<'play' | 'code' | 'edit'>('play');
  
  // SDK version tracking
  const [projectSDKVersion, setProjectSDKVersion] = useState<string | null>(null);
  const [editorSDKVersion, setEditorSDKVersion] = useState<string | null>(null);
  const [showSDKUpgradeBanner, setShowSDKUpgradeBanner] = useState(false);
  
  // Use custom hook for project management
  const { projectData, loadingProjectData, gameContent, loadProjectData, accessDenied, accessDeniedMessage } = useProjectManagement(projectId);
  
  // Use custom hook for conversation/session management
  const {
    sessionId,
    setSessionId,
    conversationHistory,
    setConversationHistory,
    suggestions,
    setSuggestions,
    loadingSuggestions,
    fetchHistory,
    fetchSuggestions,
    handleClearHistory: clearHistory
  } = useConversation(projectId);
  
  // AI Generation state
  const [showCreatingModal, setShowCreatingModal] = useState(false);
  const [creationProgress, setCreationProgress] = useState(0);
  const [creationStatus, setCreationStatus] = useState('Creating Game...');
  const [showGenerationFailedDialog, setShowGenerationFailedDialog] = useState(false);
  const initializingProjectRef = useRef<string | null>(null); // Track which project is being initialized
  
  // AI Model state
  const [selectedModel, setSelectedModel] = useState<AIModel>('norm');
  
  // AI Style state
  const [selectedStyle, setSelectedStyle] = useState<VisualStyle>('beemi');
  
  // Chat format state (for auto-chat feature)
  const [chatFormat, setChatFormat] = useState<string>('generic');
  
  // Categories from store
  const { fetchCategories, getCategoryById } = useCategoriesStore();
  
  // Use custom hook for usage tracking
  const { usageData, fetchUsage, setUsageData } = useUsageTracking();
  
  // User state
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  
  
  // Use custom hook for edit history
  const {
    editHistory,
    loadingHistory,
    isRestoring,
    showBranchedEdits,
    setShowBranchedEdits,
    fetchEditHistory,
    handleRestoreVersion: restoreVersionFromHook
  } = useEditHistory(projectId);
  
  // Use custom hook for review management
  const {
    reviews,
    loadingReviews,
    isSubmittingReview,
    pollingReviewId,
    handleSubmitReview: submitReview,
    fetchReviews
  } = useReviewManagement(projectId, activeSection, (section) => {
    setCompletedSections(prev => {
      const newSet = new Set(prev);
      newSet.add(section);
      return newSet;
    });
  });
  
  // Streaming feature flag
  const [enableStreaming, setEnableStreaming] = useState(false);
  
  // Use custom hook for AI generation
  const aiGeneration = useAIGeneration(projectId, sessionId, enableStreaming, {
    onSessionUpdate: setSessionId,
    onHistoryRefresh: fetchHistory,
    onProjectDataRefresh: loadProjectData,
    onEditHistoryRefresh: fetchEditHistory
  });
  
  // Use custom hook for processing messages
  const { processingTime, currentProcessingMessage, processingMessageIndex} = 
    useProcessingMessages(aiGeneration.isProcessing);
  
  // SDK state
  const [bridgeReady, setBridgeReady] = useState(false);
  const gamePreviewRef = useRef<GamePreviewHandle>(null);
  const editModePreviewRef = useRef<EditModePreviewHandle>(null);
  const promptInputRef = useRef<AIPromptInputHandle>(null);
  
  // Use custom hook for screenshot management
  const {
    screenshots,
    setScreenshots,
    isCapturingScreenshot,
    handleCaptureScreenshot: captureScreenshot,
    handleCaptureAllScreenshots: captureAllScreenshots
  } = useScreenshots(projectData, gamePreviewRef);
  
  // Callback ref for handling edit mode changes save
  const applyChangesCallbackRef = useRef<((html: string) => Promise<void>) | null>(null);
  
  // Memoized callback for element selection events
  const handleElementSelected = useCallback((data: { editId: string; label: string } | null) => {
    if (data && activeTab === 'edit') {
      if (promptInputRef.current) {
        promptInputRef.current.dispatchElementSelected(data.editId, data.label);
      }
    }
  }, [activeTab]);

  // Use custom hook for visual editor
  const {
    selectedElementData,
    lockSize,
    setLockSize,
    handleTransformChange,
    handleTextChange,
    handleBackgroundChange,
    handleBorderChange,
    handleEffectsChange,
    handleSpacingChange,
    sections,
    templates
  } = useVisualEditor(editModePreviewRef, handleElementSelected);
  
  // Modal state
  const [showAssetsModal, setShowAssetsModal] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [showReviewDropdown, setShowReviewDropdown] = useState(false);
  const reviewDropdownRef = useRef<HTMLDivElement>(null);
  
  // Drag and drop state
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const dragCounterRef = useRef(0);
  
  // Prompt input focus state
  const [isPromptFocused, setIsPromptFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  
  // Preview scale state for responsive scaling
  const [previewScale, setPreviewScale] = useState(1);
  const previewContainerRef = useRef<HTMLDivElement>(null);
  
  // Right sidebar tab state
  const [rightSidebarTab, setRightSidebarTab] = useState<'chat' | 'history'>('chat');
  
  // Emulator visibility state
  const [isEmulatorVisible, setIsEmulatorVisible] = useState(true);
  
  // TikTok overlay visibility state
  const [isTikTokOverlayVisible, setIsTikTokOverlayVisible] = useState(false);
  
  // Preview mute state
  const [isPreviewMuted, setIsPreviewMuted] = useState(false);
  
  // Edit mode changes tracking
  const [hasEditModeChanges, setHasEditModeChanges] = useState(false);
  
  // Code editor changes tracking
  const [savedCode, setSavedCode] = useState(STARTER_TEMPLATE);
  const [hasCodeChanges, setHasCodeChanges] = useState(false);
  
  // Apply changes loading state
  const [isApplyingChanges, setIsApplyingChanges] = useState(false);
  
  // === PHASE 2.3: Re-enable Project Loading ===
  const LOAD_SAVED_PROJECT = true;

  // Check if streaming is enabled (feature detection)
  useEffect(() => {
    // Check server feature flags
    // For now, enable streaming by default (can be controlled via env var)
    const streamingEnabled = import.meta.env.VITE_ENABLE_STREAMING_AI === 'true' || true;
    setEnableStreaming(streamingEnabled);
    console.log('🌊 Streaming AI:', streamingEnabled ? 'ENABLED' : 'DISABLED');
  }, []);

  // Load recommended SDK version on mount
  useEffect(() => {
    const loadRecommendedSDK = async () => {
      try {
        const { version } = await sdkService.getRecommendedSDK();
        setEditorSDKVersion(version);
        console.log('📦 Editor will use SDK version:', version);
      } catch (error) {
        console.error('Failed to load recommended SDK:', error);
        setEditorSDKVersion('2.1.3'); // Fallback
      }
    };
    
    loadRecommendedSDK();
  }, []);

  // Compare SDK versions when project loads
  useEffect(() => {
    if (projectData?.metadata?.sdkVersion && editorSDKVersion) {
      const projectVersion = projectData.metadata.sdkVersion;
      setProjectSDKVersion(projectVersion);
      
      // Show banner if versions differ
      if (projectVersion !== editorSDKVersion) {
        setShowSDKUpgradeBanner(true);
        console.log(`📦 SDK Version Difference - Project: ${projectVersion}, Editor: ${editorSDKVersion}`);
      }
    }
  }, [projectData, editorSDKVersion]);

  // Update preview when code changes
  useEffect(() => {
    setPreviewKey(prev => prev + 1);
  }, [code]);

  // Track code changes for Apply Changes button
  useEffect(() => {
    setHasCodeChanges(code !== savedCode);
  }, [code, savedCode]);

  // Calculate scale for preview container
  useEffect(() => {
    const calculateScale = () => {
      if (previewContainerRef.current) {
        const container = previewContainerRef.current;
        const availableHeight = container.offsetHeight;
        const availableWidth = container.offsetWidth;
        const contentHeight = 813;
        const contentWidth = 900;
        const scale = Math.min(1, availableWidth / contentWidth, availableHeight / contentHeight);
        setPreviewScale(scale);
      }
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, []);


  // Handle click outside review dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (reviewDropdownRef.current && !reviewDropdownRef.current.contains(event.target as Node)) {
        setShowReviewDropdown(false);
      }
    };

    if (showReviewDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showReviewDropdown]);

  // Page-level drag and drop detection
  useEffect(() => {
    const handlePageDragEnter = (e: DragEvent) => {
      e.preventDefault();
      
      // Check if dragging files (not text or other data)
      if (e.dataTransfer?.types.includes('Files')) {
        dragCounterRef.current++;
        if (dragCounterRef.current === 1) {
          setIsDraggingFiles(true);
        }
      }
    };

    const handlePageDragOver = (e: DragEvent) => {
      e.preventDefault();
      // Keep the drag active
    };

    const handlePageDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) {
        setIsDraggingFiles(false);
      }
    };

    const handlePageDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDraggingFiles(false);
      
      // If drop was already handled by AssetBrowser, don't process again
      if ((e as any).handledByAssetBrowser) {
        return;
      }
      
      // Get dropped files
      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length > 0) {
        // Open assets modal and pass files
        setDroppedFiles(files);
        setShowAssetsModal(true);
        
        // Track analytics
        analyticsService.track({
          name: 'files_dropped_on_editor',
          properties: {
            file_count: files.length,
            file_types: files.map(f => f.type),
            project_id: projectId
          }
        });
      }
    };

    // Add listeners to document
    document.addEventListener('dragenter', handlePageDragEnter);
    document.addEventListener('dragover', handlePageDragOver);
    document.addEventListener('dragleave', handlePageDragLeave);
    document.addEventListener('drop', handlePageDrop);

    return () => {
      document.removeEventListener('dragenter', handlePageDragEnter);
      document.removeEventListener('dragover', handlePageDragOver);
      document.removeEventListener('dragleave', handlePageDragLeave);
      document.removeEventListener('drop', handlePageDrop);
    };
  }, [projectId]);

  // Handle suggestions display with delay for non-empty chat history
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    // Reset suggestions visibility when focus or prompt changes
    if (!isPromptFocused || prompt.trim() !== '' || !hasUserInteracted) {
      setShowSuggestions(false);
      return;
    }

    // If focused and prompt is empty AND user has interacted
    if (isPromptFocused && prompt.trim() === '' && hasUserInteracted) {
      const hasHistory = conversationHistory.length > 0;
      
      if (hasHistory) {
        // With chat history: 3 second delay
        timer = setTimeout(() => {
          setShowSuggestions(true);
        }, 3000);
      } else {
        // No chat history: show immediately
        setShowSuggestions(true);
      }
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isPromptFocused, prompt, conversationHistory.length, hasUserInteracted]);

  // Load project on mount
  useEffect(() => {
    const loadProject = async () => {
      if (!projectId) {
        console.log('⚠️ No project ID provided in URL');
        return;
      }
      
      setIsLoadingCode(true);
      try {
        if (LOAD_SAVED_PROJECT) {
          // Try to load existing project code from GCS
          const response = await apiService.get(`/editor/project/${projectId}`);
          if (response.data.success && response.data.code) {
            setCode(response.data.code);
            setSavedCode(response.data.code); // Track saved version
            setHasCodeChanges(false); // No changes yet
            console.log('✅ Loaded project:', projectId, `(${response.data.code.length} bytes)`);
            
            // Fetch edit history AFTER project loads (so auto-repair edits are already created)
            fetchEditHistory();
            
            // Track editor opened
            analyticsService.track({
              name: 'editor_opened',
              properties: {
                project_id: projectId,
                has_existing_code: true,
                code_size_bytes: response.data.code?.length || 0,
                tab: activeTab
              }
            });
            
            return; // Success, project loaded
          }
        }
      } catch (err) {
        console.log('No existing project found, will create on first save');
      } finally {
        setIsLoadingCode(false);
      }
    };
    loadProject();
  }, [projectId]);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Load screenshots from project metadata
  useEffect(() => {
    if (projectData?.metadata?.screenshots) {
      setScreenshots(projectData.metadata.screenshots);
    }
  }, [projectData]);

  // Update section completion status based on project data
  useEffect(() => {
    if (!projectData) return;
    
    const sectionsComplete = projectData.metadata?.sectionsComplete;
    if (!sectionsComplete) return;
    
    const newCompletedSections = new Set<'game' | 'assets' | 'description' | 'review'>();
    
    // Sync all sections
    if (sectionsComplete.game) newCompletedSections.add('game');
    if (sectionsComplete.assets) newCompletedSections.add('assets');
    if (sectionsComplete.description) newCompletedSections.add('description');
    if (sectionsComplete.review) newCompletedSections.add('review');
    
    setCompletedSections(newCompletedSections);
    console.log('✅ Synced section completion status:', sectionsComplete);
  }, [projectData]);

  // Load AI preferences from project data
  useEffect(() => {
    if (!projectData) return;
    
    // Load saved AI preferences
    if (projectData.metadata?.aiPreferences) {
      setSelectedModel(projectData.metadata.aiPreferences.model || 'norm');
      setSelectedStyle(projectData.metadata.aiPreferences.style || 'beemi');
      console.log('✅ Loaded AI preferences:', projectData.metadata.aiPreferences);
    } else if (projectData.metadata?.createProjectParameters?.style) {
      // Fallback: Load from creation parameters
      setSelectedStyle(projectData.metadata.createProjectParameters.style);
      console.log('✅ Loaded style from creation parameters:', projectData.metadata.createProjectParameters.style);
    }
    
    // Load saved chat format
    if (projectData.metadata?.livestreamEmulator?.chatFormat) {
      setChatFormat(projectData.metadata.livestreamEmulator.chatFormat);
      console.log('✅ Loaded chat format from metadata:', projectData.metadata.livestreamEmulator.chatFormat);
    }
  }, [projectData]);

  // Save AI preferences when changed
  useEffect(() => {
    if (!projectId) return;
    
    const savePreferences = async () => {
      try {
        await apiService.put(`/editor/project/${projectId}/ai-preferences`, {
          model: selectedModel,
          style: selectedStyle
        });
        console.log('✅ Saved AI preferences:', { model: selectedModel, style: selectedStyle });
      } catch (error) {
        console.error('Failed to save AI preferences:', error);
      }
    };
    
    // Debounce to avoid excessive saves
    const timeoutId = setTimeout(savePreferences, EDITOR_CONFIG.PREFERENCES_SAVE_DELAY);
    return () => clearTimeout(timeoutId);
  }, [selectedModel, selectedStyle, projectId]);

  // Detect new projects and generate initial game code
  useEffect(() => {
    if (!projectData || !projectId) return;
    
    // Check if this is a new project with metadata (template, style, interactivity)
    const metadata = projectData.metadata;
    const createParams = metadata?.createProjectParameters;
    const hasMetadata = createParams?.template || createParams?.style;
    const hasGeneratedCode = metadata?.hasGeneratedInitialCode;
    
    // Only generate if:
    // 1. Has metadata (template/style from creation)
    // 2. Hasn't generated code yet (flag not set)
    // 3. Modal is not currently showing (prevents duplicate calls)
    // 4. No existing edit history (prevents re-initialization of existing projects)
    // 5. Not already initializing this project (prevents race conditions)
    if (hasMetadata && !hasGeneratedCode && !showCreatingModal && editHistory.length === 0 && initializingProjectRef.current !== projectId) {
      console.log('🎮 New project detected, generating initial code:', createParams);
      initializingProjectRef.current = projectId; // Mark as initializing
      generateInitialGameCode(metadata);
    }
  }, [projectData, projectId, editHistory, showCreatingModal]);


  // Fetch current user on mount
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await authService.getCurrentUser();
        setCurrentUser(userData.user || userData);
      } catch (err) {
        console.error('Failed to fetch current user:', err);
      }
    };
    
    fetchUser();
  }, []);


  // Auto-fetch history when switching to chat tab in right sidebar
  useEffect(() => {
    if (rightSidebarTab === 'chat' && sessionId) {
      fetchHistory();
    }
  }, [rightSidebarTab, sessionId, fetchHistory]);




  // Toast notification helper
  const showToast = (
    type: 'success' | 'error' | 'info' | 'warning',
    message: string,
    duration = 3000
  ) => {
    setToastConfig({ type, message, isVisible: true });
    setTimeout(() => {
      setToastConfig(null);
    }, duration);
  };

  const handleSubmitReview = async (editorVersionId: string) => {
    await submitReview(
      editorVersionId,
      (msg) => showToast('success', msg),
      (msg) => showToast('error', msg)
    );
  };

  // Validate if project details are filled
  const validateProjectDetails = (): boolean => {
    if (!gameContent?.content) return false;
    
    // Check required fields
    const hasTitle = gameContent.content.name && gameContent.content.name.trim().length > 0;
    const hasDescription = gameContent.content.description && gameContent.content.description.trim().length > 0;
    const hasCategory = gameContent.content.category && gameContent.content.category.length > 0;
    
    return hasTitle && hasDescription && hasCategory;
  };

  // Handle publish button click - now opens modal for review submission flow
  const handlePublishClick = async () => {
    if (!projectId) return;
    
    // Always show the publish modal
    // The modal will handle saving details + submitting for review
    console.log('📝 Opening publish modal for review submission');
    setShowPublishModal(true);
  };

  // Handle preview button click — uploads current edit to GCS and opens webplay popup
  const handlePreviewClick = async () => {
    if (!projectId || isGeneratingPreview) return;

    setIsGeneratingPreview(true);
    try {
      const result = await studioPreviewService.generatePreview(projectId);

      const webplayBase = import.meta.env.VITE_WEBPLAY_URL || 'https://play.beemi.app';
      const previewPageUrl = `${webplayBase}/studio-preview/${projectId}?psu=${encodeURIComponent(result.previewUrl)}`;

      window.open(
        previewPageUrl,
        'beemi-studio-preview',
        'width=500,height=750,resizable=yes'
      );
    } catch (error: any) {
      console.error('❌ Preview generation failed:', error);
      showToast('error', error.message || 'Failed to generate preview');
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  // Handle screenshot capture
  const handleCaptureScreenshot = async () => {
    await captureScreenshot(
      (msg) => showToast('success', msg),
      (msg) => showToast('error', msg)
    );
  };

  // Capture all game sections triggered from the description modal.
  // Ensures game section + play tab are active so the iframe is mounted.
  const captureScreenshotFromDetails = () => {
    const alreadyReady = activeSection === 'game' && activeTab === 'play';
    setActiveSection('game');
    setActiveTab('play');
    const delay = alreadyReady ? 300 : 900;
    setTimeout(() => {
      captureAllScreenshots(
        (msg) => showToast('success', msg),
        (msg) => showToast('error', msg)
      );
    }, delay);
  };

  // Helper: Get status label for display
  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'pending': 'Pending',
      'in_progress': 'In Review',
      'passed': 'Reviewed',
      'failed': 'Issues Found',
      'published': 'Published',
      'outdated': 'Outdated',
      'needs_manual_review': 'Manual Review Required'
    };
    return labels[status] || status;
  };

  // Helper: Get status color for display
  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'pending': 'text-gray-400',
      'in_progress': 'text-blue-400',
      'passed': 'text-green-400',
      'failed': 'text-red-400',
      'published': 'text-gray-500',
      'outdated': 'text-gray-500',
      'needs_manual_review': 'text-yellow-400'
    };
    return colors[status] || 'text-gray-400';
  };

  // Helper: Format timestamp
  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return '0 min ago';
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  // Handle apply edit mode changes
  const handleApplyEditModeChanges = async () => {
    if (!projectId || !editModePreviewRef.current) return;
    
    setIsApplyingChanges(true);
    
    try {
      const iframe = editModePreviewRef.current.iframeRef.current?.contentWindow;
      if (!iframe) return;
      
      // Get edits for counting
      const edits = getEditsFromIframe(iframe);
      const editCount = Object.keys(edits).length;
      
      // Set up callback to handle the clean HTML from applyChanges
      applyChangesCallbackRef.current = async (cleanHtml: string) => {
        try {
          // Save to server with edit count
          const response = await apiService.put(`/editor/project/${projectId}`, {
            code: cleanHtml,
            summary: `[${editCount} Edit Change${editCount !== 1 ? 's' : ''}]`,
            source: 'user_edit',
            sdkVersion: editorSDKVersion
          });
          
          if (response.data.success) {
            setSavedCode(cleanHtml);
            setHasCodeChanges(false);
            setHasEditModeChanges(false);
            
            // Refresh edit history
            await fetchEditHistory();
            
            console.log('✅ Edit mode changes saved:', editCount, 'edits');
          }
        } catch (error) {
          console.error('Failed to save edit mode changes:', error);
          showToast('error', 'Failed to save changes');
        } finally {
          setIsApplyingChanges(false);
          applyChangesCallbackRef.current = null;
        }
      };
      
      // Call the proven applyChanges method - it will:
      // 1. Get edits from iframe localStorage
      // 2. Apply edits to preprocessed HTML (IDs match)
      // 3. Strip edit attributes properly
      // 4. Clear iframe localStorage
      // 5. Call onApplyChanges with clean HTML
      // 6. Our callback will then save to server
      editModePreviewRef.current.applyChanges();
      
    } catch (error) {
      console.error('Failed to apply edit mode changes:', error);
      showToast('error', 'Failed to apply changes');
      setIsApplyingChanges(false);
      applyChangesCallbackRef.current = null;
    }
  };

  // Handle apply code changes
  const handleApplyCodeChanges = async () => {
    if (!projectId) return;
    
    setIsApplyingChanges(true);
    try {
      // Count line differences
      const savedLines = savedCode.split('\n');
      const currentLines = code.split('\n');
      
      let changedLines = 0;
      const maxLines = Math.max(savedLines.length, currentLines.length);
      
      for (let i = 0; i < maxLines; i++) {
        if (savedLines[i] !== currentLines[i]) {
          changedLines++;
        }
      }
      
      // Save to server with line count
      const response = await apiService.put(`/editor/project/${projectId}`, {
        code: code,
        summary: `[${changedLines} Code Change${changedLines !== 1 ? 's' : ''}]`,
        source: 'user_edit',
        sdkVersion: editorSDKVersion
      });
      
      if (response.data.success) {
        setSavedCode(code);
        setHasCodeChanges(false);
        
        // Refresh edit history
        await fetchEditHistory();
        
        console.log('✅ Code changes saved:', changedLines, 'lines');
      }
    } catch (error) {
      console.error('Failed to save code changes:', error);
      showToast('error', 'Failed to save changes');
    } finally {
      setIsApplyingChanges(false);
    }
  };

  const handleClearHistory = async () => {
    if (!sessionId) return;
    
    if (!confirm('Are you sure you want to start a new conversation?')) {
      return;
    }
    
    try {
      const response = await apiService.delete(`/editor/session/${sessionId}/history`);
      if (response.data.success && response.data.sessionId) {
        // Update to the new session ID
        setSessionId(response.data.sessionId);
        setConversationHistory([]);
        console.log('New session created:', response.data.sessionId);
        
        // Fetch suggestions for new session (will have defaults)
        fetchSuggestions(response.data.sessionId);
      }
    } catch (err) {
      console.error('Failed to clear history:', err);
      showToast('error', 'Failed to start new conversation');
    }
  };

  const handleSuggestionClick = (text: string) => {
    // Fill the prompt input with the suggestion text
    setPrompt(text);
    setHasUserInteracted(false);
    console.log('Applied suggestion:', text);
  };

  const handleAddAssetToPrompt = (assetId: string, assetName: string) => {
    if (promptInputRef.current) {
      promptInputRef.current.insertAsset(assetId, assetName);
      promptInputRef.current.focus();
    }
    // Close the asset modal
    setShowAssetsModal(false);
    setDroppedFiles([]);
  };

  const handleRestoreVersion = async (editId: string) => {
    await restoreVersionFromHook(
      editId,
      (newCode) => {
        setCode(newCode);
        setSavedCode(newCode);
        setHasCodeChanges(false);
      },
      () => showToast('success', 'Version restored successfully'),
      (errorMsg) => showToast('error', errorMsg)
    );
  };


  // Helper function to mark initialization as attempted (prevents infinite retries)
  const markInitializationAttempted = async () => {
    if (!projectId) return;
    
    try {
      // Update metadata to set hasGeneratedInitialCode flag
      await apiService.put(`/editor/project-data/metadata`, {
        projectId,
        metadata: {
          hasGeneratedInitialCode: true,
          initializationFailed: true,
          initializationFailedAt: new Date().toISOString()
        }
      });
      
      // Reload project data to get updated metadata
      await loadProjectData();
      
      console.log('⚠️ Marked initialization as attempted (failed)');
    } catch (error) {
      console.error('Failed to mark initialization as attempted:', error);
      // Don't throw - this is a safety mechanism, not critical
    }
  };

  // SSE initialization with progress tracking
  const initializeProjectWithSSE = async (projectId: string): Promise<{ usedFallback?: boolean }> => {
    return new Promise((resolve, reject) => {
      const token = localStorage.getItem('beemi_access_token');
      const url = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/api/editor/project/${projectId}/initialize`;
      
      console.log('📡 Connecting to SSE for initialization:', url);

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({})
      }).then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let resultData: any = null;
        let buffer = ''; // Buffer for incomplete lines

        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            console.log('📡 SSE stream ended');
            break;
          }

          // Append chunk to buffer
          buffer += decoder.decode(value, { stream: true });
          
          // Process complete lines (lines ending with \n)
          const lines = buffer.split('\n');
          
          // Keep the last incomplete line in buffer
          buffer = lines.pop() || '';

          for (const line of lines) {
            // Log ALL lines received
            if (line.trim()) {
              console.log('📡 SSE Line:', line.substring(0, 100) + (line.length > 100 ? '...' : ''));
            }
            
            if (line.startsWith('event: ')) {
              console.log('📌 SSE Event Type:', line.substring(7));
            }
            
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.substring(6));
                console.log('📦 SSE Data Parsed:', data.type || data.phase || 'complete');
                
                // Handle progress events
                if (data.type === 'create_game_progress') {
                  console.log(`📊 Progress Event: ${data.percentage}%`);
                  setCreationProgress(data.percentage);
                  setCreationStatus('Generating game code...');
                } else if (data.phase === 'details_generation') {
                  console.log('🎨 Details Generation Phase:', data.message);
                  setCreationProgress(100);
                  setCreationStatus(data.message || 'Generating project details...');
                } else if (data.phase === 'details_complete') {
                  console.log('✅ Details Complete:', data.message);
                  setCreationStatus('Project details generated!');
                } else if (data.phase === 'details_skipped') {
                  console.log('⏭️  Details Skipped:', data.message);
                } else if (data.phase === 'fallback') {
                  // Server initiated fallback
                  console.log('🔄 Server applying fallback:', data.message);
                  setCreationStatus('Applying fallback template...');
                } else if (data.success && data.editId) {
                  // Complete event (code will be fetched separately)
                  console.log('✅ Complete Event - Edit ID:', data.editId, 'Code size:', data.codeSize, 'Used fallback:', data.usedFallback);
                  resultData = { usedFallback: data.usedFallback };
                  setCreationStatus('Complete!');
                  
                  // Update usage data if provided
                  if (data.budget) {
                    setUsageData(data.budget);
                    console.log('💰 Usage updated:', data.budget);
                  }
                } else if (data.error || data.message) {
                  // Error event - server decided something failed
                  console.error('❌ Server Error:', data.error || data.message);
                  throw new Error(data.error || data.message);
                } else {
                  console.log('⚠️ Unknown data format:', data);
                }
              } catch (e) {
                if (e instanceof Error && e.message.includes('JSON')) {
                  // JSON parse error - log but don't fail (might be incomplete chunk)
                  console.warn('⚠️ Failed to parse SSE data (might be incomplete):', e.message);
                } else {
                  // Re-throw non-parse errors
                  throw e;
                }
              }
            }
          }
        }

        if (!resultData) {
          throw new Error('Initialization did not complete successfully');
        }

        console.log('✅ SSE initialization completed successfully');
        resolve(resultData);
      }).catch((error) => {
        console.error('❌ SSE initialization failed:', error);
        reject(error);
      });
    });
  };

  // Generate initial game code from template metadata
  const generateInitialGameCode = async (metadata: any) => {
    if (!projectId) return;
    
    console.log('🎨 Initializing project with metadata:', metadata);
    setShowCreatingModal(true);
    setCreationProgress(0);
    
    try {
      // Call initialization endpoint with SSE streaming
      const result = await initializeProjectWithSSE(projectId);
      
      console.log('✅ Initialization complete');
      
      // Refresh project data
      await loadProjectData();
      
      // Load the generated code into the editor
      try {
        const response = await apiService.get(`/editor/project/${projectId}`);
        if (response.data.success && response.data.code) {
          setCode(response.data.code);
          setSavedCode(response.data.code);
          setHasCodeChanges(false);
          console.log('✅ Loaded generated code:', response.data.code.length, 'bytes');
        }
      } catch (codeErr) {
        console.error('Failed to load generated code:', codeErr);
      }
      
      // Refresh edit history
      await fetchEditHistory();
      
      // Show appropriate message based on result
      if (result?.usedFallback) {
        showToast('warning', '⚠️ AI generation failed - blank template applied. You can add your code manually.');
      } else {
        showToast('success', '🎮 Game initialized successfully!');
      }
    } catch (err: any) {
      // Server-side error (fallback also failed)
      console.error('Failed to initialize project:', err);
      showToast('error', 'Failed to initialize project');
      await markInitializationAttempted();
    } finally {
      setShowCreatingModal(false);
      setCreationProgress(0);
      setCreationStatus('Creating Game...');
    }
  };

  const handleApplyPrompt = async (cleanedPrompt: string, promptItems: any[]) => {
    await aiGeneration.handleApplyPrompt(
      cleanedPrompt,
      code,
      selectedModel,
      selectedStyle,
      {
        onCodeUpdate: (newCode) => {
          setCode(newCode);
          setSavedCode(newCode);
        },
        onPromptClear: () => {
          setPrompt('');
          setHasUserInteracted(false);
        },
        onUsageUpdate: setUsageData,
        onSuggestionsUpdate: setSuggestions,
        onChatFormatUpdate: setChatFormat,
        onSessionUpdate: setSessionId,
        onHistoryRefresh: fetchHistory,
        onProjectDataRefresh: loadProjectData,
        onEditHistoryRefresh: fetchEditHistory,
        onError: (msg) => showToast('error', msg),
        onOptimisticUpdate: (msg) => setConversationHistory(prev => [...prev, msg])
      },
      'generate',
      undefined,
      promptItems
    );
  };

  const handleFixErrors = async () => {
    if (consoleErrors.length === 0) return;

    // Format errors for AI
    const observedErrors = consoleErrors.join('\n\n---\n\n');
    
    console.log('🔧 Sending errors to Fix Pipeline:', observedErrors);

    await aiGeneration.handleApplyPrompt(
      'Fix the issues listed in observedErrors',
      code,
      selectedModel,
      selectedStyle,
      {
        onCodeUpdate: (newCode) => {
          setCode(newCode);
          setSavedCode(newCode);
          setConsoleErrors([]); // Clear errors after fix is applied
        },
        onPromptClear: () => {
          setPrompt('');
          setHasUserInteracted(false);
        },
        onUsageUpdate: setUsageData,
        onSuggestionsUpdate: setSuggestions,
        onChatFormatUpdate: setChatFormat,
        onSessionUpdate: setSessionId,
        onHistoryRefresh: fetchHistory,
        onProjectDataRefresh: loadProjectData,
        onEditHistoryRefresh: fetchEditHistory,
        onError: (msg) => showToast('error', msg),
        onOptimisticUpdate: (msg) => setConversationHistory(prev => [...prev, msg])
      },
      'fix', // Use fix pipeline
      observedErrors // Pass console errors
    );
  };

  // Get project name and categories from game content
  const projectName = gameContent?.content?.name || gameContent?.name || 'Untitled';
  
  // Get tags (which are the selected categories) or fallback to single category
  const categoryTags = gameContent?.content?.tags || [];
  const projectCategoryText = categoryTags.length > 0 
    ? categoryTags.map((tag: string) => {
        const cat = getCategoryById(tag);
        return cat ? cat.displayName : tag;
      }).join(', ')
    : 'No category';

  // Determine the current edit's review status
  const getCurrentEditReviewStatus = () => {
    if (editHistory.length === 0) {
      return { status: 'none', label: 'Publish', disabled: true }; // No edits = can't publish
    }

    const latestEdit = editHistory[0];
    const latestReview = reviews.find(r => r.editor_edit_id === latestEdit.id);

    if (!latestReview) {
      // No review yet = show enabled Publish button (replaces "Needs Review")
      return { status: 'publish', label: 'Publish', disabled: false };
    }

    switch (latestReview.status) {
      case 'in_progress':
      case 'pending':
        return { status: 'in_review', label: 'In Review', disabled: true };
      case 'passed':
        // Review passed, waiting for auto-publish = show enabled Publish
        return { status: 'publish', label: 'Publish', disabled: false };
      case 'published':
        return { status: 'published', label: 'Published', disabled: true };
      case 'failed':
        // Review failed = show enabled Publish to resubmit
        return { status: 'publish', label: 'Publish', disabled: false };
      default:
        return { status: 'publish', label: 'Publish', disabled: false };
    }
  };

  const currentEditStatus = getCurrentEditReviewStatus();

  // Early return: Show access denied panel if user doesn't have permission
  if (accessDenied && projectId) {
    return <AccessDeniedPanel projectId={projectId} errorMessage={accessDeniedMessage} />;
  }

  return (
    <div className="h-screen flex flex-col bg-gray-900 relative">
      {/* Drag and Drop Overlay */}
      <AnimatePresence>
        {isDraggingFiles && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] pointer-events-none"
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            
            {/* Content */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center gap-4 bg-white dark:bg-gray-800 rounded-2xl p-12 shadow-2xl border-4 border-dashed border-purple-500 dark:border-purple-400">
                <div 
                  className="w-24 h-24 rounded-full flex items-center justify-center"
                  style={{ background: FILL_GRADIENT }}
                >
                  <FolderOpen className="w-12 h-12 text-white" />
                </div>
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    Drop files to upload
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Release to open asset manager
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* SDK Upgrade Banner */}
      {showSDKUpgradeBanner && (
        <div className="fixed top-16 left-1/2 transform -translate-x-1/2 z-50 
                        bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg 
                        flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
          <Sparkles className="w-4 h-4" />
          <span className="text-sm font-medium">
            Testing with SDK v{editorSDKVersion} (project uses v{projectSDKVersion})
          </span>
          <button
            onClick={() => setShowSDKUpgradeBanner(false)}
            className="ml-2 text-white/80 hover:text-white"
          >
            ×
          </button>
        </div>
      )}
      
      {/* Tabs Header - Full Page */}
      <div className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border flex-shrink-0">
        <div className="flex items-center justify-between px-6 h-14">
          {/* BeemiStudio Logo on the left */}
          <Link to="/" className="group flex items-center gap-2 flex-shrink-0">
            <img src={beemiLogo} alt="Beemi" className="w-8 h-8 flex-shrink-0" />
            <span className="whitespace-nowrap flex items-center">
              <span className="relative inline-block">
                {/* Solid color layer */}
                <span className="font-bold lowercase text-2xl text-gray-900 dark:text-dark-text transition-opacity duration-300 group-hover:opacity-0">
                  Beemi
                </span>
                {/* Gradient layer */}
                <span 
                  className="font-bold lowercase text-2xl absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    backgroundImage: LINE_GRADIENT,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Beemi
                </span>
              </span>
              <span className="font-light uppercase text-base text-gray-900 dark:text-dark-text transition-colors duration-300 group-hover:text-[#FF00CF]">Studio</span>
            </span>
          </Link>

          {/* Tabs in the center */}
          <div className="flex justify-center flex-1 h-14">
            <button
              onClick={() => {
                const previousTab = activeTab;
                setActiveTab('play');
                if (previousTab !== 'play') {
                  analyticsService.track({
                    name: 'editor_tab_changed',
                    properties: {
                      from_tab: previousTab,
                      to_tab: 'play',
                      project_id: projectId
                    }
                  });
                }
              }}
              className={`relative flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors ${
                activeTab === 'play'
                  ? 'text-gray-900 dark:text-dark-text'
                  : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-900 dark:hover:text-dark-text'
              }`}
            >
              <Play className="w-4 h-4" />
              Play
              {activeTab === 'play' && (
                <div 
                  className="absolute -bottom-px left-0 right-0 h-1"
                  style={{ background: LINE_GRADIENT }}
                />
              )}
            </button>
            <button
              onClick={() => {
                const previousTab = activeTab;
                setActiveTab('edit');
                if (previousTab !== 'edit') {
                  analyticsService.track({
                    name: 'editor_tab_changed',
                    properties: {
                      from_tab: previousTab,
                      to_tab: 'edit',
                      project_id: projectId
                    }
                  });
                  analyticsService.track({
                    name: 'edit_mode_toggled',
                    properties: {
                      enabled: true,
                      project_id: projectId
                    }
                  });
                }
              }}
              className={`relative flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors ${
                activeTab === 'edit'
                  ? 'text-gray-900 dark:text-dark-text'
                  : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-900 dark:hover:text-dark-text'
              }`}
            >
              <Pencil className="w-4 h-4" />
              Edit
              {activeTab === 'edit' && (
                <div 
                  className="absolute -bottom-px left-0 right-0 h-1"
                  style={{ background: LINE_GRADIENT }}
                />
              )}
            </button>
            <button
              onClick={() => setActiveTab('code')}
              className={`relative flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors ${
                activeTab === 'code'
                  ? 'text-gray-900 dark:text-dark-text'
                  : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-900 dark:hover:text-dark-text'
              }`}
            >
              <Code className="w-4 h-4" />
              Code
              {activeTab === 'code' && (
                <div 
                  className="absolute -bottom-px left-0 right-0 h-1"
                  style={{ background: LINE_GRADIENT }}
                />
              )}
            </button>
          </div>

          {/* Section Icons and Publish Button on the right */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Assets Button */}
            <button
              onClick={() => setShowAssetsModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-gray-900 dark:text-dark-text bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors border border-gray-200 dark:border-dark-border"
              title="Assets"
            >
              <FolderOpen className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm font-medium">
                Assets
              </span>
            </button>

            {/* Game Icon + Name Tag (Edit Details) */}
            <button
              onClick={() => setShowDescriptionModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-gray-900 dark:text-dark-text bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors border border-gray-200 dark:border-dark-border"
              title="Edit Project Details"
            >
              {gameContent?.content?.icon_url ? (
                <img 
                  src={gameContent.content.icon_url} 
                  alt={projectName}
                  className="w-6 h-6 rounded-md object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-6 h-6 rounded-md bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
              )}
              <span className="text-sm font-medium max-w-[140px] truncate">
                {projectName}
              </span>
            </button>

            {/* Spacer */}
            <div className="w-px h-6 bg-gray-300 dark:bg-gray-700"></div>

            {/* Review Icon with Dropdown */}
            <div className="relative" ref={reviewDropdownRef}>
              <button
                onClick={() => setShowReviewDropdown(!showReviewDropdown)}
                className={`relative p-2 rounded-lg transition-colors ${
                  showReviewDropdown
                    ? 'text-gray-900 dark:text-dark-text bg-gray-100 dark:bg-gray-800'
                    : 'text-gray-600 dark:text-dark-text-muted hover:text-gray-900 dark:hover:text-dark-text hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
                title="Reviews"
              >
                <ClipboardList className="w-4 h-4" />
                
                {/* Status Indicators */}
                {currentEditStatus.status === 'in_review' && (
                  <>
                    {/* Fixed gradient background with rotating mask */}
                    <div 
                      className="absolute inset-0 flex items-center justify-center pointer-events-none"
                      style={{ margin: '0px' }}
                    >
                      <div
                        className="w-full h-full"
                        style={{
                          background: FILL_GRADIENT,
                          maskImage: 'url(#reviewSpinnerMask)',
                          WebkitMaskImage: 'url(#reviewSpinnerMask)',
                          maskSize: '100% 100%',
                          WebkitMaskSize: '100% 100%',
                          maskRepeat: 'no-repeat',
                          WebkitMaskRepeat: 'no-repeat',
                          maskPosition: 'center',
                          WebkitMaskPosition: 'center',
                        }}
                      />
                    </div>
                    
                    {/* Hidden SVG for mask definition */}
                    <svg viewBox="0 0 32 32" width="0" height="0" className="absolute">
                      <defs>
                        <mask id="reviewSpinnerMask" maskUnits="userSpaceOnUse" x="0" y="0" width="32" height="32">
                          <rect width="32" height="32" fill="black" />
                          <g className="animate-spin" style={{ transformOrigin: '16px 16px', animationDuration: '2s' }}>
                            <circle
                              cx="16"
                              cy="16"
                              r="13"
                              stroke="white"
                              strokeWidth="2"
                              fill="none"
                              strokeDasharray="81.68"
                              strokeDashoffset="20"
                              strokeLinecap="round"
                            />
                          </g>
                        </mask>
                      </defs>
                    </svg>
                  </>
                )}
                
                {currentEditStatus.status === 'rejected' && (
                  <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-red-500 rounded-full flex items-center justify-center">
                    <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                )}
                
                {currentEditStatus.status === 'published' && (
                  <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>

              {/* Review Dropdown */}
              {showReviewDropdown && (
                <div className="absolute right-0 top-full mt-2 w-[420px] max-h-[600px] bg-white dark:bg-gray-900 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
                  {/* Header */}
                  <div className="px-4 py-3">
                    <h3 className="text-lg text-gray-900 dark:text-white">Reviews</h3>
                  </div>

                  {/* Content */}
                  <div className="overflow-y-auto max-h-[540px]">
                    {loadingReviews ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full"></div>
                      </div>
                    ) : reviews.length === 0 && editHistory.length === 0 ? (
                      <div className="text-center py-12 px-4">
                        <ClipboardList className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-600 dark:text-gray-400 mb-2">No reviews yet</p>
                        <p className="text-sm text-gray-500 dark:text-gray-500">Make changes to your game to create your first review</p>
                      </div>
                    ) : (
                      <div className="p-6 space-y-3">
                        {/* Submit Button for Latest Edit */}
                        {editHistory.length > 0 && !reviews.some(r => r.editor_edit_id === editHistory[0]?.id) && (() => {
                          const latestEdit = editHistory[0];
                          const versionNumber = reviews.length + 1;
                          
                          return (
                            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Version {versionNumber}</h4>
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">Not Reviewed</span>
                              </div>
                              
                              {latestEdit && (
                                <div className="flex items-start gap-2 mb-3 text-gray-700 dark:text-gray-300">
                                  <div className="w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
                                    {latestEdit.model === 'norm' ? 'B' : latestEdit.model === 'exp' ? 'O' : latestEdit.model === 'eco' ? 'S' : 'U'}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm font-medium">
                                      {latestEdit.model === 'norm' ? 'Bee' : latestEdit.model === 'exp' ? 'Owl' : latestEdit.model === 'eco' ? 'Squirrel' : 'User'}
                                    </span>
                                    <span className="text-sm text-gray-600 dark:text-gray-400 block">{latestEdit.summary}</span>
                                  </div>
                                </div>
                              )}
                              
                              <button 
                                onClick={() => handleSubmitReview(latestEdit.id)}
                                disabled={isSubmittingReview}
                                className="w-full px-4 py-2 text-sm font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-white hover:shadow-lg hover:scale-105 active:scale-95"
                                style={isSubmittingReview ? {} : { background: FILL_GRADIENT }}
                              >
                                {isSubmittingReview ? 'Submitting...' : 'Submit for Review'}
                              </button>
                            </div>
                          );
                        })()}
                        
                        {/* Existing Reviews */}
                        {reviews.map((review, index) => {
                          const version = editHistory.find(v => v.id === review.editor_edit_id);
                          const reviewNumber = reviews.length - index;
                          
                          return (
                            <div key={review.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-base font-semibold text-gray-900 dark:text-white">Version {reviewNumber}</h4>
                                <span className={`text-xs font-medium px-2 py-1 rounded ${getStatusColor(review.status)}`}>
                                  {getStatusLabel(review.status)}
                                </span>
                              </div>
                              
                              {version && (
                                <div className="flex items-start gap-2 mb-3 text-gray-700 dark:text-gray-300">
                                  <div className="w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
                                    {version.model === 'norm' ? 'B' : version.model === 'exp' ? 'O' : version.model === 'eco' ? 'S' : 'U'}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm font-medium">
                                      {version.model === 'norm' ? 'Bee' : version.model === 'exp' ? 'Owl' : version.model === 'eco' ? 'Squirrel' : 'User'}
                                    </span>
                                    <span className="text-sm text-gray-600 dark:text-gray-400 block">{version.summary}</span>
                                  </div>
                                </div>
                              )}
                              
                              {/* Review Status Details */}
                              {review.status === 'in_progress' && review.progress && (
                                <div className="bg-white dark:bg-gray-700 rounded-lg p-3 mt-2">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                      {review.progress.currentStep?.name || 'In Review'}
                                    </span>
                                    {review.progress.currentStep && (
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {review.progress.completed + 1}/{review.progress.total}
                                      </span>
                                    )}
                                  </div>
                                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                                    <div 
                                      className="bg-blue-500 h-1.5 rounded-full transition-all duration-300" 
                                      style={{ width: `${review.progress.currentStep?.progress || 0}%` }}
                                    ></div>
                                  </div>
                                </div>
                              )}
                              
                              {review.status === 'passed' && (
                                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 flex items-center mt-2">
                                  <span className="text-xs font-medium text-green-700 dark:text-green-400">
                                    ✓ Review passed - Game will be published automatically
                                  </span>
                                </div>
                              )}
                              
                              {review.status === 'published' && (
                                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 flex items-center justify-between mt-2">
                                  <span className="text-xs font-medium text-green-700 dark:text-green-400">
                                    {review.published_at ? `Published ${formatTimeAgo(review.published_at)}` : 'Published'}
                                  </span>
                                  <div className="flex items-center gap-1 text-green-600 dark:text-green-500">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="text-xs font-medium">Live</span>
                                  </div>
                                </div>
                              )}
                              
                              {review.status === 'failed' && review.result && (
                                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mt-2">
                                  <div className="text-xs font-medium text-red-700 dark:text-red-400 mb-1">Issues Found</div>
                                  {review.result.issues && Array.isArray(review.result.issues) ? (
                                    <ul className="space-y-1 text-xs text-red-600 dark:text-red-500">
                                      {review.result.issues.slice(0, 3).map((issue: string, idx: number) => (
                                        <li key={idx}>• {issue}</li>
                                      ))}
                                      {review.result.issues.length > 3 && (
                                        <li className="text-red-500 dark:text-red-400">+ {review.result.issues.length - 3} more</li>
                                      )}
                                    </ul>
                                  ) : (
                                    <p className="text-xs text-red-600 dark:text-red-500">{review.result.message || 'Review failed'}</p>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Preview Button */}
            <button
              onClick={handlePreviewClick}
              disabled={isGeneratingPreview}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 border ${
                isGeneratingPreview
                  ? 'border-gray-300 dark:border-gray-700 text-gray-400 dark:text-gray-500 bg-transparent cursor-not-allowed'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 hover:scale-105 active:scale-95'
              }`}
              title="Preview the current version in webplay"
            >
              {isGeneratingPreview ? 'Preparing...' : 'Preview'}
            </button>

            {/* Publish Button */}
            <button
              onClick={handlePublishClick}
              disabled={currentEditStatus.disabled || isPublishing}
              className={`px-8 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
                currentEditStatus.disabled || isPublishing
                  ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500'
                  : 'text-white hover:shadow-lg hover:scale-105 active:scale-95'
              }`}
              style={currentEditStatus.disabled || isPublishing ? {} : { background: FILL_GRADIENT }}
            >
              {isPublishing ? 'Publishing...' : currentEditStatus.label}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex min-h-0">
      {/* Game Section */}
      {activeSection === 'game' && (
        <>
          {/* Center Column */}
          <div className="flex-1 flex flex-col min-w-0">
        {/* Main Tabbed Content Area */}
        <div className="flex-1 overflow-hidden bg-gray-50 dark:bg-gray-900 relative">
          {/* Back Button - Top Left */}
          <button
            onClick={() => navigate('/projects')}
            className="absolute top-3 left-3 z-50 flex items-center gap-1.5 px-2.5 py-1.5 text-sm bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white rounded-md transition-colors border border-gray-300 dark:border-gray-700"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          {/* Play Tab - Keep mounted, hide with CSS */}
          <div className={`h-full ${activeTab === 'play' ? '' : 'hidden'}`}>
            <div ref={previewContainerRef} className="h-full flex items-center justify-center p-4 overflow-hidden">
              <div className="flex items-start gap-4" style={{ 
                width: isEmulatorVisible ? '813px' : '500px', // 500 + 313 when emulator visible
                transform: `scale(${previewScale})`,
                transformOrigin: 'center',
                transition: 'transform 0.2s ease-out'
              }}>
                {/* Game Preview with Action Row */}
                <div className="flex flex-col" style={{ width: '500px' }}>
                  {/* Action Row */}
                  <div className="flex-shrink-0 px-3 py-1.5 rounded-lg">
                    <div className="flex items-center justify-between gap-1.5">
                      {/* Left: Restart and Screenshot */}
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => setPreviewKey(prev => prev + 1)}
                          className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                          title="Restart Game"
                        >
                          <RotateCw className="w-5 h-5" />
                        </button>
                        <button 
                          onClick={handleCaptureScreenshot}
                          disabled={isCapturingScreenshot}
                          className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                          title="Take Screenshot"
                        >
                          {isCapturingScreenshot ? (
                            <div className="w-5 h-5 border-2 border-gray-900 dark:border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Camera className="w-5 h-5" />
                          )}
                        </button>
                      </div>

                      {/* Right: Mute Toggle, TikTok Overlay Toggle, and Emulator Toggle */}
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => setIsPreviewMuted(!isPreviewMuted)}
                          className={`p-2 rounded-md transition-colors ${
                            isPreviewMuted
                              ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600'
                              : 'bg-primary-600 text-white hover:bg-primary-700'
                          }`}
                          title={isPreviewMuted ? "Unmute Preview" : "Mute Preview"}
                        >
                          {isPreviewMuted ? (
                            <VolumeX className="w-5 h-5" />
                          ) : (
                            <Volume2 className="w-5 h-5" />
                          )}
                        </button>
                        <button 
                          onClick={() => setIsTikTokOverlayVisible(!isTikTokOverlayVisible)}
                          className={`p-2 rounded-md transition-colors ${
                            isTikTokOverlayVisible
                              ? 'bg-primary-600 text-white hover:bg-primary-700'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600'
                          }`}
                          title={isTikTokOverlayVisible ? "Hide TikTok Overlay" : "Show TikTok Overlay"}
                        >
                          <img 
                            src={tiktokIcon} 
                            alt="TikTok"
                            className={`w-5 h-5 object-contain ${
                              isTikTokOverlayVisible
                                ? '[filter:brightness(2)]'
                                : '[filter:brightness(0.1)_saturate(0)] dark:[filter:none]'
                            }`}
                          />
                        </button>
                        <button 
                          onClick={() => setIsEmulatorVisible(!isEmulatorVisible)}
                          className={`p-2 rounded-md transition-colors ${
                            isEmulatorVisible
                              ? 'bg-primary-600 text-white hover:bg-primary-700'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600'
                          }`}
                          title={isEmulatorVisible ? "Hide Emulator" : "Show Emulator"}
                        >
                          <MessagesSquare className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Game Preview */}
                  <div className="rounded-xl shadow-2xl overflow-hidden relative" style={{ width: '500px', height: '638px' }}>
                    <GamePreview
                      ref={gamePreviewRef}
                      code={code}
                      projectId={projectId}
                      previewKey={previewKey}
                      onBridgeReady={setBridgeReady}
                      onConsoleError={setConsoleErrors}
                      sdkVersion={editorSDKVersion || undefined}
                      isMuted={isPreviewMuted}
                    />
                    
                    {/* TikTok Overlay */}
                    {isTikTokOverlayVisible && (
                      <div 
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          backgroundImage: `url(${emulatorOverlay})`,
                          backgroundSize: 'contain',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat',
                        }}
                      />
                    )}
                  </div>
                </div>
                
                {/* Livestream Simulator (matches preview height) */}
                {isEmulatorVisible && (
                  <div className="rounded-xl shadow-2xl overflow-hidden" style={{ width: '313px', height: '638px', marginTop: '48px' }}>
                    <LivestreamSimulator 
                      iframeRef={gamePreviewRef.current?.iframeRef || { current: null }}
                      bridgeReady={bridgeReady}
                      chatFormat={chatFormat}
                      sdkVersion={editorSDKVersion || undefined}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Code Tab - Keep mounted, hide with CSS */}
          <div className={`h-full flex flex-col ${activeTab === 'code' ? '' : 'hidden'}`}>
            {/* Code Editor Container */}
            <div className="flex-1 flex justify-center overflow-hidden">
              <div className="w-full max-w-[900px] px-4 flex flex-col">
                {/* Action Row */}
                <div className="flex-shrink-0 py-3 flex items-center justify-end">
                  <button 
                    onClick={handleApplyCodeChanges}
                    disabled={!hasCodeChanges || isApplyingChanges}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                      !hasCodeChanges || isApplyingChanges
                        ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500'
                        : 'text-white hover:shadow-lg hover:scale-105 active:scale-95'
                    }`}
                    style={!hasCodeChanges || isApplyingChanges ? {} : { background: FILL_GRADIENT }}
                    title="Apply Changes"
                  >
                    {isApplyingChanges ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Apply Changes
                      </>
                    )}
                  </button>
                </div>
                
                {/* Code Editor */}
                <div className="flex-1 pb-4 overflow-hidden">
                  <CodeEditor code={code} onChange={setCode} />
                </div>
              </div>
            </div>
          </div>

          {/* Edit Tab */}
          <div className={`h-full ${activeTab === 'edit' ? '' : 'hidden'}`}>
            <div className="h-full flex items-center justify-center p-4 overflow-hidden">
              <div className="flex items-start gap-4" style={{ 
                width: isEmulatorVisible ? '813px' : '500px', // 500 + 313 when emulator visible
                transform: `scale(${previewScale})`,
                transformOrigin: 'center',
                transition: 'transform 0.2s ease-out'
              }}>
                {/* Edit Mode Preview with Action Row */}
                <div className="flex flex-col" style={{ width: '500px' }}>
                  {/* Action Row */}
                  <div className="flex-shrink-0 px-3 py-1.5 rounded-lg">
                    <div className="flex items-center justify-between gap-1.5">
                      {/* Left: Restart and Apply Changes */}
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => setPreviewKey(prev => prev + 1)}
                          className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                          title="Restart Game"
                        >
                          <RotateCw className="w-5 h-5" />
                        </button>
                        <button 
                          onClick={handleApplyEditModeChanges}
                          disabled={!hasEditModeChanges || isApplyingChanges}
                          className="flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-500 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:text-gray-500 dark:disabled:text-gray-500 transition-colors"
                          title="Apply Changes"
                        >
                          {isApplyingChanges ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Check className="w-5 h-5" />
                              Apply Changes
                            </>
                          )}
                        </button>
                      </div>

                      {/* Right: Emulator Toggle */}
                      <button 
                        onClick={() => setIsEmulatorVisible(!isEmulatorVisible)}
                        className={`p-2 rounded-md transition-colors ${
                          isEmulatorVisible
                            ? 'bg-primary-600 text-white hover:bg-primary-700'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600'
                        }`}
                        title={isEmulatorVisible ? "Hide Emulator" : "Show Emulator"}
                      >
                        <MessagesSquare className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Edit Mode Preview */}
                  <div className="rounded-xl shadow-2xl overflow-hidden" style={{ width: '500px', height: '638px' }}>
                    <EditModePreview
                      ref={editModePreviewRef}
                      code={code}
                      projectId={projectId}
                      previewKey={previewKey}
                      onApplyChanges={async (updatedCode) => {
                        setCode(updatedCode);
                        
                        // If there's a pending callback (from Apply Changes button), execute it
                        if (applyChangesCallbackRef.current) {
                          await applyChangesCallbackRef.current(updatedCode);
                        }
                        // Note: savedCode is updated by the callback, not here
                        // This allows visual edits to show as unsaved until Apply Changes is clicked
                      }}
                      onHasChangesUpdate={setHasEditModeChanges}
                    />
                  </div>
                </div>
                
                {/* Properties Panel (matches simulator height) */}
                {isEmulatorVisible && (
                  <div className="rounded-xl shadow-2xl overflow-hidden" style={{ width: '313px', height: '708px' }}>
                    <PropertiesPanel
                      selectedElement={selectedElementData}
                      sections={sections}
                      templates={templates}
                      editModePreviewRef={editModePreviewRef}
                      onTransformChange={handleTransformChange}
                      onTextChange={handleTextChange}
                      onBackgroundChange={handleBackgroundChange}
                      onBorderChange={handleBorderChange}
                      onEffectsChange={handleEffectsChange}
                      onSpacingChange={handleSpacingChange}
                      onLockSizeChange={setLockSize}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>


          {/* Loading Code Overlay */}
          <AnimatePresence>
            {isLoadingCode && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 bg-gray-50 dark:bg-dark-bg z-50 flex flex-col items-center justify-center gap-6"
              >
                <div className="loader-bars"></div>
                <p className="text-gray-600 dark:text-dark-text-muted text-base font-medium tracking-wide">Loading code...</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
          </>
        )}

        {/* Right Sidebar - Chat & History */}
        {activeSection === 'game' && (
          <div className="w-[540px] flex-shrink-0 flex flex-col p-4 bg-gray-50 dark:bg-gray-900">
            <div className="flex-1 bg-white dark:bg-dark-surface rounded-xl shadow-lg border border-gray-200 dark:border-dark-border flex flex-col min-h-0 overflow-visible">
              {/* Tabs */}
              <div className="border-b border-gray-200 dark:border-dark-border flex flex-shrink-0 rounded-t-xl overflow-hidden">
                <button
                  onClick={() => setRightSidebarTab('chat')}
                  className={`relative flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    rightSidebarTab === 'chat'
                      ? 'text-gray-900 dark:text-dark-text'
                      : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-900 dark:hover:text-dark-text'
                  }`}
                >
                  <MessageSquare className="w-4 h-4 inline-block mr-2" />
                  Chat
                  {rightSidebarTab === 'chat' && (
                    <div 
                      className="absolute -bottom-px left-0 right-0 h-1"
                      style={{ background: LINE_GRADIENT }}
                    />
                  )}
                </button>
                <button
                  onClick={() => setRightSidebarTab('history')}
                  className={`relative flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    rightSidebarTab === 'history'
                      ? 'text-gray-900 dark:text-dark-text'
                      : 'text-gray-500 dark:text-dark-text-muted hover:text-gray-900 dark:hover:text-dark-text'
                  }`}
                >
                  <History className="w-4 h-4 inline-block mr-2" />
                  History
                  {rightSidebarTab === 'history' && (
                    <div 
                      className="absolute -bottom-px left-0 right-0 h-1"
                      style={{ background: LINE_GRADIENT }}
                    />
                  )}
                </button>
              </div>

              {/* Tab Content */}
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-b-xl">
                {/* Chat Tab */}
                {rightSidebarTab === 'chat' && (
                  <>
                    {/* Chat History */}
                    <div className="flex-1 min-h-0 overflow-hidden">
                    <ChatHistory
                      history={conversationHistory}
                      isProcessing={aiGeneration.isProcessing}
                      processingUpdateMessage={aiGeneration.processingUpdateMessage}
                      selectedModel={selectedModel}
                      selectedStyle={selectedStyle}
                      onModelChange={setSelectedModel}
                      onStyleChange={setSelectedStyle}
                      usageData={usageData}
                      onClearHistory={handleClearHistory}
                      processingTime={processingTime}
                      currentProcessingMessage={currentProcessingMessage}
                      processingMessageKey={processingMessageIndex.current}
                      currentUser={currentUser}
                      streamingChatMessages={aiGeneration.streamingChatMessages}
                      streamingCodeEdits={aiGeneration.streamingCodeEdits}
                      codeEditSummary={aiGeneration.codeEditSummary}
                      codeEditCount={aiGeneration.codeEditCount}
                      codeEditCompleteSummary={aiGeneration.codeEditCompleteSummary}
                      codeEditLinesReceived={aiGeneration.codeEditLinesReceived}
                      codeEditComplete={aiGeneration.codeEditComplete}
                      isStreaming={aiGeneration.isStreaming}
                    />
                  </div>

                  {/* Console Error Panel - Shows above prompt when errors detected */}
                  <AnimatePresence>
                    {consoleErrors.length > 0 && (
                      <ErrorPanel 
                        errorCount={consoleErrors.length}
                        onFix={handleFixErrors}
                      />
                    )}
                  </AnimatePresence>

                  {/* AI Prompt Input with Suggestions */}
                  <div className="relative flex-shrink-0">
                    {/* Suggestions Panel - Shows above prompt when focused and empty */}
                    <AnimatePresence>
                      {showSuggestions && suggestions.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="absolute bottom-full left-0 right-0"
                        >
                          <SuggestionsList
                            suggestions={suggestions}
                            onSuggestionClick={(text) => {
                              handleSuggestionClick(text);
                              setIsPromptFocused(false);
                            }}
                            isLoading={loadingSuggestions}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                    
                    <div 
                      onFocus={() => setIsPromptFocused(true)} 
                      onBlur={() => setTimeout(() => setIsPromptFocused(false), 200)}
                      onMouseDown={() => setHasUserInteracted(true)}
                    >
                      <AIPromptInput
                        ref={promptInputRef}
                        prompt={prompt}
                        onPromptChange={setPrompt}
                        onApply={handleApplyPrompt}
                        onAbort={aiGeneration.abortStream}
                        isProcessing={aiGeneration.isProcessing}
                        isStreaming={aiGeneration.isStreaming}
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                        selectedModel={selectedModel}
                        processingTime={processingTime}
                        currentProcessingMessage={currentProcessingMessage}
                        processingMessageKey={processingMessageIndex.current}
                        isLimitReached={usageData ? usageData.percentage >= 100 : false}
                        iframeRef={editModePreviewRef.current?.iframeRef}
                        projectId={projectId || ''}
                        onOpenAssets={() => setShowAssetsModal(true)}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* History Tab */}
              {rightSidebarTab === 'history' && (
                <div className="h-full flex flex-col overflow-hidden">
                  <div className="p-4 flex items-center gap-2 flex-shrink-0 border-b border-gray-200 dark:border-dark-border">
                    <h2 className="text-sm font-medium text-gray-900 dark:text-dark-text">Version History</h2>
                    {isRestoring && (
                      <span className="text-xs text-gray-600 dark:text-dark-text-muted">Restoring...</span>
                    )}
                    
                    {/* Toggle branched edits button */}
                    <button
                      onClick={() => setShowBranchedEdits(!showBranchedEdits)}
                      className="ml-auto p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                      title={showBranchedEdits ? 'Hide branched edits' : 'Show branched edits'}
                    >
                      {showBranchedEdits ? (
                        <Eye className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-gray-500 dark:text-gray-500" />
                      )}
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto min-h-0">
                    <EditHistory 
                      history={editHistory} 
                      onRestoreVersion={handleRestoreVersion}
                      currentUser={currentUser}
                      isLoading={loadingHistory}
                      showBranchedEdits={showBranchedEdits}
                    />
                  </div>
                </div>
              )}
            </div>
            </div>
          </div>
        )}

        {/* Assets Section */}
        {activeSection === 'assets' && (
          <div className="flex-1 overflow-hidden">
            {projectId ? (
              <AssetBrowser 
                projectId={projectId} 
                onSectionComplete={loadProjectData}
                currentCode={code}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-400">No project loaded</div>
              </div>
            )}
          </div>
        )}

        {/* Description Section */}
        {activeSection === 'description' && (
          <div className="flex-1 overflow-hidden">
            {projectId ? (
              <ProjectDetailsForm 
                projectId={projectId}
                code={code}
                screenshots={screenshots}
                onScreenshotsChange={setScreenshots}
                onSectionComplete={loadProjectData}
                onClose={() => setActiveSection('game')}
                skipValidation={true}
                isEditableInStudio={true}
                onCaptureScreenshot={captureScreenshotFromDetails}
                isCapturingScreenshot={isCapturingScreenshot}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-gray-400">No project loaded</div>
              </div>
            )}
          </div>
        )}

        {/* Review Section */}
        {activeSection === 'review' && (
          <ReviewSection
            reviews={reviews}
            editHistory={editHistory}
            loadingReviews={loadingReviews}
            isSubmittingReview={isSubmittingReview}
            onSubmitReview={handleSubmitReview}
            formatTimeAgo={formatTimeAgo}
            getStatusLabel={getStatusLabel}
            getStatusColor={getStatusColor}
          />
        )}

        {/* Creating Game Modal */}
        <CreatingGameModal isOpen={showCreatingModal} progress={creationProgress} status={creationStatus} />

        {/* Generation Failed Dialog */}
        <GenerationFailedDialog 
          isOpen={showGenerationFailedDialog}
          onClose={() => setShowGenerationFailedDialog(false)}
        />

        {/* Publish Modal */}
        <PublishModal
          isOpen={showPublishModal}
          onClose={() => setShowPublishModal(false)}
          gameContent={gameContent?.content || gameContent}
          screenshots={screenshots}
          onSubmitReview={handleSubmitReview}
          editHistory={editHistory}
          reviews={reviews}
          onEditDetails={() => setShowDescriptionModal(true)}
          currentUser={currentUser}
        />

        {/* Assets Modal - Wide */}
        <AnimatePresence>
          {showAssetsModal && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => {
                  setShowAssetsModal(false);
                  setDroppedFiles([]); // Clear dropped files on backdrop click
                }}
                className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              >
                {/* Modal */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden"
                >
                  {/* Gradient Line at Top */}
                  <div className="h-1 w-full" style={{ background: FILL_GRADIENT }} />
                  
                  {/* Modal Header */}
                  <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <FolderOpen className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Assets</h2>
                    </div>
                    <button
                      onClick={() => {
                        setShowAssetsModal(false);
                        setDroppedFiles([]); // Clear dropped files on close
                      }}
                      className="p-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  {/* Modal Content */}
                  <div className="flex-1 overflow-hidden">
                    {projectId && (
                      <AssetBrowser 
                        projectId={projectId} 
                        onSectionComplete={loadProjectData}
                        droppedFiles={droppedFiles}
                        onFilesProcessed={() => setDroppedFiles([])}
                        onAddToPrompt={handleAddAssetToPrompt}
                        currentCode={code}
                      />
                    )}
                  </div>
                </motion.div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Edit Project Modal */}
        {gameContent && (
          <ProjectEditModal
            isOpen={showDescriptionModal}
            onClose={() => setShowDescriptionModal(false)}
            project={gameContent}
            code={code}
            showGenerateButton={true} // Always true for editor (always studio projects)
            screenshots={screenshots}
            onScreenshotsChange={setScreenshots}
            onCaptureScreenshot={captureScreenshotFromDetails}
            isCapturingScreenshot={isCapturingScreenshot}
            onSave={async (updates) => {
              // Update the project via API
              await projectsService.updateProject(projectId!, updates);
              // Reload project data to reflect changes
              await loadProjectData();
              // Close modal
              setShowDescriptionModal(false);
              // Show success toast
              showToast('success', 'Project updated successfully');
            }}
          />
        )}

        {/* Toast Notification */}
        {toastConfig && (
          <EditorToast
            type={toastConfig.type}
            message={toastConfig.message}
            isVisible={toastConfig.isVisible}
            onDismiss={() => setToastConfig(null)}
          />
        )}
      </div> {/* End Main Content Area */}
    </div>
  );
};

export default EditorPage;

